import { Injectable, inject } from '@angular/core';
import {Observable, of, zip} from 'rxjs';
import User, {JwtPayload, Role} from './user';
import {ThuisverzorgerService} from '../../thuisverzorger/thuisverzorger.service';
import {RegioverantwoordelijkeService} from '../../regioverantwoordelijke/regioverantwoordelijke.service';
import {TherapeutService} from '../../therapeut/therapeut.service';
import {AdminService} from '../../admin/admin.service';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private thuisverzorgerService = inject(ThuisverzorgerService);
  private regioverantwoordelijkeService = inject(RegioverantwoordelijkeService);
  private therapeutService = inject(TherapeutService);
  private adminService = inject(AdminService);

  getMe$(jwtPayload: JwtPayload): Observable<User> {
    const user = User.fromJwt(jwtPayload as JwtPayload);
    if (user.isThuisverzorger()) {
      return this.thuisverzorgerService.getThuisverzorgerMe$()
        .pipe(map(thuisverzorger => this.toUser(jwtPayload, thuisverzorger)));
    } else if (user.isRegioverantwoordelijke()) {
      return this.regioverantwoordelijkeService.getRegioverantwoordelijkeMe$()
        .pipe(map(regioverantwoordelijke => this.toUser(jwtPayload, regioverantwoordelijke)));
    } else if (user.isTherapeut()) {
      return this.therapeutService.getTherapeutMe$()
        .pipe(map(therapeut => this.toUser(jwtPayload, therapeut)));
    } else if (user.isAdmin()) {
      return this.adminService.getAdminMe$()
        .pipe(map(admin => this.toUser(jwtPayload, admin)));
    } else {
      return of(user);
    }
  }

  getUsersByIds$(userIds: Array<{ id: string, nummer: string, role: Role }>): Observable<Array<User>> {
    const thuisverzorgerUsers: Observable<Array<User>> = this.thuisverzorgerService
      .getThuisverzorgersByIds$(userIds.filter(id => id.role === Role.THUISVERZORGER))
      .pipe(map(thuisverzorgers => thuisverzorgers.map(thuisverzorger => User.fromThuisverzorger(thuisverzorger))));
    const regioverantwoordelijkeUsers: Observable<Array<User>> = this.regioverantwoordelijkeService
      .getRegioverantwoordelijkenByIds$(userIds.filter(id => id.role === Role.REGIOVERANTWOORDELIJKE))
      .pipe(map(regioverantwoordelijken => regioverantwoordelijken.map(regioverantwoordelijke => User.fromRegioverantwoordelijke(regioverantwoordelijke))));
    const therapeutUsers: Observable<Array<User>> = this.therapeutService
      .getTherapeutenByIds$(userIds.filter(id => id.role === Role.THERAPEUT))
      .pipe(map(therapeuten => therapeuten.map(therapeut => User.fromTherapeut(therapeut))));
    return zip(thuisverzorgerUsers, regioverantwoordelijkeUsers, therapeutUsers)
      .pipe(map(([thuisverzorgers, reguioverantwoordelijken, therapeuten]) => [...thuisverzorgers, ...reguioverantwoordelijken, ...therapeuten]));
  }

  private toUser(
    jwtPayload: JwtPayload,
    user: { getVoornaam(): string, getAchternaam(): string, getPersoneelsnummer(): string },
  ): User {
    return User.fromJwt(
      jwtPayload,
      user.getVoornaam(),
      user.getAchternaam(),
    );
  }
}
